import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircle2, XCircle, Loader2, Settings as SettingsIcon, Shield, Server, Mail, Bot, Webhook, KeyRound, Copy, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Status({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md border ${
      ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
    }`}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {children}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 gap-4">
      <span className="text-sm text-slate-600 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right break-all">{value}</span>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-cyan-700" />} {title}
      </h2>
      <div>{children}</div>
    </div>
  );
}

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const [system, setSystem] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cur, setCur] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, message, id}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, i] = await Promise.all([
          axios.get(`${API}/admin/settings/system`),
          axios.get(`${API}/admin/settings/integrations`),
        ]);
        if (!cancelled) { setSystem(s.data); setIntegrations(i.data); }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (next1.length < 8) return toast.error("New password must be at least 8 characters");
    if (next1 !== next2) return toast.error("Passwords do not match");
    setPwSaving(true);
    try {
      await axios.post(`${API}/admin/settings/change-password`, {
        current_password: cur,
        new_password: next1,
      });
      toast.success("Password updated. Use the new password next time you sign in.");
      setCur(""); setNext1(""); setNext2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setPwSaving(false);
    }
  };

  const webhookUrl = `${BACKEND_URL}/api/webhooks/resend`;
  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copied");
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/settings/test-email`, {
        to_email: testTo || undefined,
      });
      setTestResult({
        ok: true,
        message: `Sent to ${data.to_email} (id: ${data.provider_message_id || "—"})`,
      });
      toast.success("Test email sent");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Failed to send test email";
      setTestResult({ ok: false, message: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading settings…
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>;
  }

  const resend = integrations?.resend || {};
  const assistant = integrations?.assistant || {};
  const scheduler = integrations?.scheduler || {};

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-settings">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <SettingsIcon className="w-5 h-5" /> Settings
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          System configuration, integration health, and advanced controls.
        </p>
      </div>

      <Card title="Admin profile" icon={Shield}>
        <Row label="Email" value={user?.email} />
        <Row label="Role" value={user?.role} />
        <Row label="Last login" value={user?.last_login_at ? new Date(user.last_login_at).toLocaleString() : "—"} />
        <Row label="Session TTL" value={`${system.jwt_ttl_minutes} minutes`} />
      </Card>

      <Card title="Change password" icon={KeyRound}>
        <form onSubmit={changePassword} className="space-y-3" data-testid="admin-password-form">
          <input
            type="password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            placeholder="Current password"
            required
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            data-testid="admin-password-current"
          />
          <input
            type="password"
            value={next1}
            onChange={(e) => setNext1(e.target.value)}
            placeholder="New password (min 8 characters)"
            required
            minLength={8}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            data-testid="admin-password-new"
          />
          <input
            type="password"
            value={next2}
            onChange={(e) => setNext2(e.target.value)}
            placeholder="Confirm new password"
            required
            minLength={8}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            data-testid="admin-password-confirm"
          />
          <Button
            type="submit"
            size="sm"
            disabled={pwSaving || !cur || !next1 || !next2}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            data-testid="admin-password-save"
          >
            {pwSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
            Update password
          </Button>
          <p className="text-xs text-slate-400 mt-1">
            This updates your admin hash in MongoDB. Restarting the backend with the original <code className="bg-slate-100 px-1 rounded">ADMIN_PASSWORD</code> env value would rotate it back.
          </p>
        </form>
      </Card>

      <Card title="Email provider (Resend)" icon={Mail}>
        <Row label="Resend API key" value={<Status ok={resend.configured}>{resend.configured ? "Configured" : "Not configured"}</Status>} />
        <Row label="From email" value={resend.from_email || "—"} />
        <Row label="Reply-to" value={resend.reply_to_email || "—"} />
        <Row label="Notification email" value={resend.notification_email || "—"} />
        <p className="text-xs text-slate-400 mt-3">
          To send from your verified domain (e.g. <code className="bg-slate-100 px-1 rounded">info@psy-tech.nl</code>), verify your domain in the Resend dashboard, then set <code className="bg-slate-100 px-1 rounded">FROM_EMAIL</code> in <code className="bg-slate-100 px-1 rounded">/app/backend/.env</code>.
        </p>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            Diagnostic test
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={resend.notification_email || "admin@psy-tech.nl"}
              className="flex-1 min-w-[200px] rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              data-testid="settings-test-email-input"
            />
            <Button
              size="sm"
              onClick={sendTestEmail}
              disabled={testing || !resend.configured}
              data-testid="settings-test-email-btn"
              className="bg-cyan-700 hover:bg-cyan-800 text-white"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send test email
            </Button>
          </div>
          {testResult && (
            <div
              className={`mt-3 text-xs rounded-md px-3 py-2 border flex items-start gap-2 ${
                testResult.ok
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-red-50 text-red-700 border-red-200"
              }`}
              data-testid="settings-test-email-result"
            >
              {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
              <span className="break-all">{testResult.message}</span>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-2">
            Leave the input blank to send to the notification email above. The attempt is saved to Email Logs.
          </p>
        </div>
      </Card>

      <Card title="Resend webhook" icon={Webhook}>
        <Row label="Signature verification" value={
          <Status ok={resend.webhook_secret_configured}>
            {resend.webhook_secret_configured ? "Secret configured" : "No secret (unverified)"}
          </Status>
        } />
        <Row label="Events (last 30d)" value={resend.webhook_events_30d ?? 0} />
        <Row label="Last event" value={resend.last_webhook ? `${resend.last_webhook.event_type} · ${new Date(resend.last_webhook.received_at).toLocaleString()}` : "—"} />
        <div className="mt-3 space-y-2">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Webhook endpoint URL</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-2 font-mono break-all" data-testid="resend-webhook-url">
              {webhookUrl}
            </code>
            <Button variant="outline" size="sm" onClick={copyWebhook} data-testid="resend-webhook-copy">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            In your Resend dashboard, add this URL as a webhook, subscribe to <code className="bg-slate-100 px-1 rounded">email.delivered / bounced / opened / clicked / complained</code>, then copy the signing secret into
            <code className="bg-slate-100 px-1 rounded mx-1">RESEND_WEBHOOK_SECRET</code> in <code className="bg-slate-100 px-1 rounded">/app/backend/.env</code> and restart.
          </p>
        </div>
      </Card>

      <Card title="AI Assistant" icon={Bot}>
        <Row label="Mode" value={
          <Status ok={assistant.mode !== "none"}>
            {assistant.mode === "user_key" ? "Your OPENAI_API_KEY" : assistant.mode === "emergent" ? "Emergent Universal Key" : "Not configured"}
          </Status>
        } />
        <Row label="Model" value={assistant.model} />
        <Row label="Rate limit" value={`${assistant.rate_limit_per_min}/min · ${assistant.rate_limit_per_hour}/hr`} />
        <p className="text-xs text-slate-400 mt-3">
          To switch from the Emergent Universal Key to your own OpenAI account, set <code className="bg-slate-100 px-1 rounded">OPENAI_API_KEY</code> in the backend env and restart.
        </p>
      </Card>

      <Card title="System" icon={Server}>
        <Row label="Database" value={<Status ok={integrations.database?.status === "connected"}>{integrations.database?.status || "—"}</Status>} />
        <Row label="Frontend URL" value={system.frontend_url || "—"} />
        <Row label="Make.com webhook" value={<Status ok={integrations.make_webhook?.configured}>{integrations.make_webhook?.configured ? "Configured" : "Not configured"}</Status>} />
        <Row label="Tracker IP salt" value={<Status ok={integrations.tracker?.ip_salt_configured}>{integrations.tracker?.ip_salt_configured ? "Custom salt" : "Default (change it!)"}</Status>} />
        <Row label="Auto-publish AI posts" value={integrations.auto_publish_ai_posts ? "Enabled" : "Disabled"} />
        <Row label="Scheduler" value={<Status ok={scheduler.running}>{scheduler.running ? "Running" : "Stopped"}</Status>} />
        {scheduler.jobs?.length > 0 && (
          <div className="pt-2 text-xs text-slate-500">
            Next run: {scheduler.jobs[0].next_run ? new Date(scheduler.jobs[0].next_run).toLocaleString() : "—"}
          </div>
        )}
      </Card>

      <Card title="Privacy &amp; analytics">
        <p className="text-xs text-slate-500 leading-relaxed">
          The lightweight backend tracker stores: hashed IP (SHA-256, salted), an anonymous visitor id and session id, page path, referrer, UTM tags, device/browser, and language preference. Raw IP addresses are <strong>never</strong> stored. Plausible Analytics runs separately on the public site and stores no cookies.
        </p>
      </Card>
    </div>
  );
}
